import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFENSE_MODULES, MODULE_INFO, WEAPON_INFO, WEAPON_TYPES, formatGameDuration,
  type DefenseModule, type FleetClient, type ShipOrderClient, type ShipyardClass, type ShipyardOverview, type ShipyardQuote, type WeaponType,
} from '@galaxis/shared';
import { useLiveResources } from '../../economy/live-store.ts';
import { useGameStore } from '../../store/game-store.ts';
import { fetchFleets, fetchResources } from '../../api/galaxy.ts';
import { fetchShipyard, fetchShipyardQuote, orderShips } from '../../api/shipyard.ts';
import { ApiError } from '../../api/client.ts';
import { SHIP_CLASS_ICONS, SHIP_CLASS_LABELS, newIdempotencyKey } from './fleet-format.ts';
import { useNow } from './FleetIcons.tsx';
import { blockerLines, finishedOrders, progressOf, remainingLabel, upkeepLabel } from './shipyard-format.ts';

const CLASSES: ShipyardClass[] = ['corvette', 'frigate', 'cruiser', 'construction', 'scout'];
const TOUCH = { minHeight: 44 } as const;
const FIELD = 'mt-1 w-full rounded bg-slate-800 border border-slate-600 text-slate-200 text-sm px-2';

interface Props {
  fleets: FleetClient[];
  /** Pre-selected shipyard system / destination fleet (from a fleet's "Hajó gyártása" shortcut). */
  initialSystemId?: string | null;
  initialFleetId?: string | null;
  onClose: () => void;
  onSelectFleet: (id: string) => void;
}

/**
 * Fleet-view shipyard: pick the system, the class and how many, read the server's price/time/upkeep
 * quote, order, watch the queue count down. Nothing is computed here: every number comes from
 * /api/shipyard. A repeated tap re-sends the same idempotency key (until the order succeeded).
 */
export function FleetShipyard({ fleets, initialSystemId, initialFleetId, onClose, onSelectFleet }: Props) {
  const { setFleets, setResources } = useGameStore();
  const resources = useLiveResources();
  const now = useNow();
  const [overview, setOverview] = useState<ShipyardOverview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [systemId, setSystemId] = useState<string>(initialSystemId ?? '');
  const [shipClass, setShipClass] = useState<ShipyardClass>('corvette');
  const [quantity, setQuantity] = useState(1);
  const [weapon, setWeapon] = useState<WeaponType>('kinetic');
  const [module, setModule] = useState<DefenseModule>('none');
  const [targetFleetId, setTargetFleetId] = useState<string>(initialFleetId ?? '');
  const [quote, setQuote] = useState<ShipyardQuote | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const keyRef = useRef(newIdempotencyKey('yard'));
  const prevOrders = useRef<Array<ShipOrderClient & { systemName: string }>>([]);
  const quoteSeq = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const ov = await fetchShipyard();
      const gone = finishedOrders(prevOrders.current, ov.orders);
      if (gone.length > 0) {
        const first = gone[0]!;
        setNotice(gone.length === 1
          ? `Kész: ${first.shipName} (${SHIP_CLASS_LABELS[first.shipClass] ?? first.shipClass}) — ${first.systemName}, ${first.targetFleetName ?? 'tartalékflotta'}`
          : `Kész: ${gone.length} hajó — ${first.systemName}, ${first.targetFleetName ?? 'tartalékflotta'}`);
        const [f, r] = await Promise.all([fetchFleets(), fetchResources()]);
        setFleets(f.fleets); setResources(r);
      }
      prevOrders.current = ov.orders;
      setOverview(ov);
      setLoadError(null);
      setSystemId(cur => cur && ov.systems.some(s => s.id === cur) ? cur : (ov.systems[0]?.id ?? ''));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'A hajógyár nem tölthető be.');
    }
  }, [setFleets, setResources]);

  useEffect(() => { void refresh(); }, [refresh, fleets.length]);
  // Poll as a fallback to the WebSocket, and right when the earliest order is due
  useEffect(() => {
    const t = setInterval(() => void refresh(), 10_000);
    return () => clearInterval(t);
  }, [refresh]);
  const nextDue = overview?.orders.map(o => o.completesAt).filter((c): c is string => !!c).sort()[0] ?? null;
  useEffect(() => {
    if (!nextDue) return;
    const t = setTimeout(() => void refresh(), Math.max(300, new Date(nextDue).getTime() - Date.now() + 800));
    return () => clearTimeout(t);
  }, [nextDue, refresh]);

  // Idle fleets at the chosen system can receive the new ships; a stale choice falls back to "reserve"
  const targets = useMemo(() => fleets.filter(f => f.currentSystemId === systemId && f.status === 'idle'), [fleets, systemId]);
  useEffect(() => { if (targetFleetId && !targets.some(f => f.id === targetFleetId)) setTargetFleetId(''); }, [targets, targetFleetId]);

  const warship = shipClass === 'corvette' || shipClass === 'frigate' || shipClass === 'cruiser';
  const metalKey = Math.floor((resources?.metal ?? 0) / 10);
  const energyKey = Math.floor((resources?.energy ?? 0) / 10);
  const ordersKey = overview?.orders.length ?? 0;
  useEffect(() => {
    if (!systemId) return;
    const seq = ++quoteSeq.current;
    const t = setTimeout(() => {
      fetchShipyardQuote({
        systemId, shipClass, quantity,
        ...(warship && weapon !== 'kinetic' ? { weapon } : {}),
        ...(warship && module !== 'none' ? { module } : {}),
        ...(targetFleetId ? { targetFleetId } : {}),
      }).then(q => { if (seq === quoteSeq.current) setQuote(q); }).catch(() => { if (seq === quoteSeq.current) setQuote(null); });
    }, 120);
    return () => clearTimeout(t);
  }, [systemId, shipClass, quantity, weapon, module, targetFleetId, warship, metalKey, energyKey, ordersKey]);

  // A changed order is a new intent: new idempotency key
  useEffect(() => { keyRef.current = newIdempotencyKey('yard'); }, [systemId, shipClass, quantity, weapon, module, targetFleetId]);

  const unlockedW = new Set(overview?.unlocked.weapons ?? ['kinetic']);
  const unlockedM = new Set(overview?.unlocked.modules ?? ['none']);
  const max = overview?.maxQuantity ?? 10;
  const systemName = overview?.systems.find(s => s.id === systemId)?.name ?? '';

  async function place() {
    if (busy || !quote?.canOrder) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await orderShips({
        systemId, shipClass, quantity,
        ...(warship && weapon !== 'kinetic' ? { weapon } : {}),
        ...(warship && module !== 'none' ? { module } : {}),
        ...(targetFleetId ? { targetFleetId } : {}),
        idempotencyKey: keyRef.current,
      });
      setNotice(`${res.orders.length} × ${SHIP_CLASS_LABELS[shipClass]} megrendelve — ${systemName}.`);
      keyRef.current = newIdempotencyKey('yard');
      setQuantity(1);
      const [f, r] = await Promise.all([fetchFleets(), fetchResources()]);
      setFleets(f.fleets); setResources(r);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? e.message : 'A megrendelés nem sikerült.');
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  const orders = overview?.orders ?? [];
  const mine = orders.filter(o => !systemId || o.systemId === systemId);

  return (
    <section className="space-y-4 pb-24" data-testid="shipyard" aria-label="Hajógyártás">
      <div className="space-y-2">
        <button type="button" onClick={onClose} className="text-sm text-cyan-400" style={TOUCH} data-testid="shipyard-close">← Flották</button>
        <h2 className="text-lg font-semibold text-slate-100">🏭 Hajógyártás</h2>
      </div>

      {loadError && <p role="alert" className="text-sm text-red-300">{loadError}</p>}
      {!overview && !loadError && <p className="text-sm text-slate-500">Betöltés…</p>}

      {overview && (
        <>
          <div className="sci-fi-panel rounded-xl p-3 space-y-3">
            <label className="block text-xs text-slate-400">
              Gyártó rendszer
              <select aria-label="Gyártó rendszer" data-testid="yard-system" value={systemId} onChange={e => setSystemId(e.target.value)} className={FIELD} style={TOUCH}>
                {overview.systems.map(s => <option key={s.id} value={s.id}>{s.name}{s.isHome ? ' (otthon)' : ''}</option>)}
              </select>
            </label>

            <div role="radiogroup" aria-label="Hajóosztály" className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="yard-classes">
              {CLASSES.map(c => {
                const locked = c === 'cruiser' && !overview.unlocked.cruiser;
                const active = c === shipClass;
                return (
                  <button key={c} type="button" role="radio" aria-checked={active} data-testid={`yard-class-${c}`} onClick={() => setShipClass(c)}
                    className="rounded-lg border text-sm px-2 text-left flex items-center gap-2"
                    style={{ ...TOUCH, borderColor: active ? '#a78bfa' : '#475569', background: active ? 'rgba(167,139,250,0.15)' : 'transparent', color: locked ? '#64748b' : '#e2e8f0' }}>
                    <span aria-hidden>{SHIP_CLASS_ICONS[c]}</span>
                    <span>{SHIP_CLASS_LABELS[c]}{locked ? ' 🔒' : ''}</span>
                  </button>
                );
              })}
            </div>

            {warship && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="block text-xs text-slate-400">
                  Fegyver
                  <select aria-label="Fegyver" data-testid="yard-weapon" value={weapon} onChange={e => setWeapon(e.target.value as WeaponType)} className={FIELD} style={TOUCH}>
                    {WEAPON_TYPES.map(w => <option key={w} value={w} disabled={!unlockedW.has(w)}>{WEAPON_INFO[w].name}{unlockedW.has(w) ? '' : ' (zárolt)'}</option>)}
                  </select>
                </label>
                <label className="block text-xs text-slate-400">
                  Védelmi modul
                  <select aria-label="Védelmi modul" data-testid="yard-module" value={module} onChange={e => setModule(e.target.value as DefenseModule)} className={FIELD} style={TOUCH}>
                    {DEFENSE_MODULES.map(m => <option key={m} value={m} disabled={!unlockedM.has(m)}>{MODULE_INFO[m].name}{unlockedM.has(m) ? '' : ' (zárolt)'}</option>)}
                  </select>
                </label>
              </div>
            )}

            <div className="flex items-end gap-3">
              <div className="text-xs text-slate-400">
                Darabszám
                <div className="mt-1 flex items-center gap-1">
                  <button type="button" aria-label="Kevesebb" data-testid="yard-qty-dec" disabled={quantity <= 1} onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="rounded-lg border border-slate-600 text-slate-200 text-lg w-11 disabled:opacity-40" style={TOUCH}>−</button>
                  <output data-testid="yard-qty" aria-label="Darabszám" className="w-12 text-center text-base font-mono text-slate-100">{quantity}</output>
                  <button type="button" aria-label="Több" data-testid="yard-qty-inc" disabled={quantity >= max} onClick={() => setQuantity(q => Math.min(max, q + 1))}
                    className="rounded-lg border border-slate-600 text-slate-200 text-lg w-11 disabled:opacity-40" style={TOUCH}>+</button>
                </div>
              </div>
              <label className="block text-xs text-slate-400 flex-1 min-w-0">
                Az új hajók ide kerülnek
                <select aria-label="Célflotta" data-testid="yard-target" value={targetFleetId} onChange={e => setTargetFleetId(e.target.value)} className={FIELD} style={TOUCH}>
                  <option value="">Tartalékflotta (új, ha nincs)</option>
                  {targets.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </label>
            </div>
          </div>

          <div className="sci-fi-panel rounded-xl p-3 space-y-2" data-testid="yard-quote" aria-live="polite">
            {!quote ? <p className="text-sm text-slate-500">Árajánlat betöltése…</p> : (
              <>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                  <dt className="text-slate-400">Költség</dt>
                  <dd className="text-right font-mono text-slate-100" data-testid="yard-cost">{quote.total.metal} fém · {quote.total.energy} energia</dd>
                  <dt className="text-slate-400">Építési idő</dt>
                  <dd className="text-right font-mono text-slate-100" data-testid="yard-time">{formatGameDuration(quote.timeMs)}</dd>
                  <dt className="text-slate-400">Fenntartás</dt>
                  <dd className="text-right font-mono text-amber-300" data-testid="yard-upkeep">{upkeepLabel(quote.upkeepDelta)}</dd>
                  {(shipClass === 'corvette' || shipClass === 'frigate' || shipClass === 'cruiser') && (
                    <>
                      <dt className="text-slate-400">Ellátási keret</dt>
                      <dd className={`text-right font-mono ${quote.supply.overAfter > 0 ? 'text-amber-300' : 'text-slate-100'}`} data-testid="yard-supply">
                        {quote.supply.shipsAfter} / {quote.supply.cap} hajó{quote.supply.overAfter > 0 ? ` (+${quote.supply.overAfter} a kereten felül)` : ''}
                      </dd>
                    </>
                  )}
                  {quote.slotsLeft !== null && (
                    <>
                      <dt className="text-slate-400">Szabad hely</dt>
                      <dd className="text-right font-mono text-slate-100" data-testid="yard-slots">{quote.slotsLeft}</dd>
                    </>
                  )}
                </dl>
                {quote.quantity > 1 && <p className="text-[11px] text-slate-500">A {quote.quantity} hajó párhuzamosan épül, egyszerre készülnek el.</p>}
                {quote.blockers.length > 0 && (
                  <ul className="text-xs text-red-300 space-y-0.5" data-testid="yard-blockers">
                    {blockerLines(quote).map(l => <li key={l}>⛔ {l}</li>)}
                  </ul>
                )}
              </>
            )}
            <button type="button" data-testid="yard-order" onClick={() => void place()} disabled={busy || !quote?.canOrder}
              className="w-full rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ ...TOUCH, minHeight: 48, background: 'rgba(167,139,250,0.2)', color: '#ddd6fe', border: '1px solid rgba(167,139,250,0.5)' }}>
              {busy ? 'Megrendelés…' : `Megrendelés: ${quantity} × ${SHIP_CLASS_LABELS[shipClass]}`}
            </button>
            {error && <p role="alert" data-testid="yard-error" className="text-xs text-red-300">{error}</p>}
            {notice && <p role="status" data-testid="yard-notice" className="text-xs text-emerald-300">{notice}</p>}
          </div>

          <div className="space-y-2" data-testid="yard-queue">
            <h3 className="text-[11px] uppercase tracking-wide text-slate-500">Épülő hajók{systemName ? ` — ${systemName}` : ''} ({mine.length})</h3>
            {mine.length === 0 && <p className="text-sm text-slate-500" data-testid="yard-queue-empty">Nincs épülő hajó ebben a rendszerben.</p>}
            <ul className="space-y-2">
              {mine.map(o => {
                const target = o.targetFleetId ? fleets.find(f => f.id === o.targetFleetId) : null;
                return (
                  <li key={o.id} data-testid="yard-order-item" data-order-id={o.id} className="sci-fi-panel rounded-xl p-3 space-y-1.5 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-100 truncate">{SHIP_CLASS_ICONS[o.shipClass]} {o.shipName} <span className="text-slate-500">· {SHIP_CLASS_LABELS[o.shipClass]}</span></span>
                      <span className="font-mono text-cyan-300 flex-shrink-0" data-testid="yard-countdown">{remainingLabel(o, now)}</span>
                    </div>
                    <div className="h-1.5 rounded bg-slate-800 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progressOf(o, now) * 100)}>
                      <div className="h-full" style={{ width: `${Math.round(progressOf(o, now) * 100)}%`, background: '#a78bfa' }} />
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs text-slate-400">
                      <span>→ {o.targetFleetName ?? 'Tartalékflotta'}</span>
                      {target && <button type="button" className="text-cyan-400" style={TOUCH} onClick={() => onSelectFleet(target.id)}>Flotta megnyitása</button>}
                    </div>
                  </li>
                );
              })}
            </ul>
            {orders.length > mine.length && <p className="text-[11px] text-slate-500">További {orders.length - mine.length} megrendelés épül más rendszereken.</p>}
          </div>
        </>
      )}
    </section>
  );
}
